import React, { useRef, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { goToTop } from '_helpers';
import { colors } from '_styles';
import Slider from 'react-slick';
import images from '_images';
import { _ } from '_locales';
import moment from 'moment';

import { alert as alertSetup } from '_actions';

import goToCollectionRequirements from '../helpers';

// import style
const Container = styled.div`
    width: 100%;

   & .slick-list {
      height: ${({ eventsContainerHeight }) => `${eventsContainerHeight}px !important`};
    }
`;

const Header = styled.div`
    width: 100%;
    display: flex;
    margin-bottom: 25px;
    box-sizing: border-box;
`;

const Date = styled.p`
    flex: 1;
    margin: 0;
    padding: 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: calc(100% - 120px);
    font-size: 16px !important;
     
`;

const LinkContainer = styled.div`
    flex: 1;
    text-align: right;
`;

const Button = styled.button`
    margin: 0;
    padding: 0;
    text-decoration: none;
    border: none;
    outline: none;
     
    font-size: 14px !important;
    color: ${colors.RC_ACTION};
    cursor: pointer;
    background-color: #fff;
    position: relative;

    &:before {
        width: 16px;
        height: 16px;
        content: ' ';
        background-image: url(${images.calendar.changeZone});
        position: absolute;
        left: -20px;
        top: -1px;
    }

    &:focus {
        outline: none;
    }
`;

const InnerLink = styled.span`
  padding: 0em;
  cursor: pointer;
  width: 100%;
  display: inline-block;
  position: relative;
  box-sizing: border-box;

  ${Button}:focus > & {
    box-shadow: 0 0 2px 2px #51a7e8;
    border-radius: 5px;
  }

  &:focus {
    outline: none;
  }
`;

const Main = styled.a`
    width: 100%;
    padding: 0;
    display: flex;
    border: none;
    background-color: transparent;
    text-align: left;
    cursor: pointer;
    border: none; 

    color: #000 !important;
    text-decoration: none !important;

    &:hover {
      text-decoration: none !important;
      color: #000 !important;
    }

    &:not(:nth-child(2)) {
      margin-top: 10px;
      padding-top: 15px;
      border-top: 1px solid #cccccc;
    }
`;

const CollectionIcon = styled.img`
    flex: 1;
    max-width: 30px !important;
    height: 30px !important;
    border-radius: 15px !important;
    padding: 5px !important;
    box-sizing: border-box;
    margin-top: 5px !important;
    margin-right: 17px !important;
`;

const CollectionContent = styled.div`
    flex: 1;
    max-width: calc(100% - 83px);
    margin-right: 17px !important;
`;

const CollectionArrow = styled.img`
    flex: 1;
    max-width: 19px !important;
    height: 50px !important;
`;

const Title = styled.h4`
    margin: 0;
    font-size: 16px !important;
`;

const Time = styled.h5`
    margin: 0;
    font-weight: 500 !important;
    margin: 6px 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 13px !important; 
`;

// end style

const EventSlider = ({
  selectedMonthsEvents,
  collection,
  curYear,
  selectedMonth,
  eventInfo,
  setIsModalOpen,
  setModalAlertingMessage,
  currentMonthEvents
}) => {
  const isFirstLoad = useRef(true);
  const sliderRef = useRef();
  const [slideIndex, setSlideIndex] = useState(0);
  const [isHoliday, setIsHoliday] = useState(false);
  const [clientXonMouseDown, setClientXonMouseDown] = useState(null);
  const [clientYonMouseDown, setClientYonMouseDown] = useState(null);
  const [eventsContainerHeight, setEventsContainerHeight] = useState('auto');
  const history = useHistory();

  const onMouseDownHandler = (e) => {
    setClientXonMouseDown(e.clientX);
    setClientYonMouseDown(e.clientY);
    e.preventDefault();
  };

  const onClickHandler = (e, id, collection, history) => {
    e.stopPropagation();
    if (
      clientXonMouseDown !== e.clientX
      || clientYonMouseDown !== e.clientY
    ) {
      e.preventDefault();
    } else {
      goToCollectionRequirements(id, collection, history, setIsHoliday);
    }
  };

  useEffect(() => {
    if (isFirstLoad.current) {
      const createEventWeekNumbersArray = Object.keys(currentMonthEvents).map((item) => {
        const modifiedDay = item.length === 1 ? `0${item}` : item;
        const modifiedMonth = (selectedMonth + 1).toString().length === 1 ? `0${selectedMonth + 1}` : `${selectedMonth + 1}`;

        // retrieve current weeks number
        return moment(`${curYear}-${modifiedMonth}-${modifiedDay}`).format('W');
      });

      const currentWeeksIndex = createEventWeekNumbersArray.findIndex((item) => item === moment().format('W'));

      if (currentWeeksIndex) {
        sliderRef.current.slickGoTo(currentWeeksIndex);
      } else {
        sliderRef.current.slickGoTo(0);
      }

      isFirstLoad.current = false;
      return;
    }
    sliderRef.current.slickGoTo(eventInfo.day);
  }, [eventInfo]);

  useEffect(() => {
    if (isHoliday) {
      setIsModalOpen(true);
      setModalAlertingMessage(_('cal.holiday_no_collection'));
      setIsHoliday(false);
    }
  }, [isHoliday]);

  useEffect(() => {
    // adjust the events container height dynamically.
    if (Object.values(selectedMonthsEvents).length > 0) {
      setEventsContainerHeight(100 + Object.values(selectedMonthsEvents)[slideIndex].length * 66);
    }
  }, [slideIndex]);

  const content = Object.keys(selectedMonthsEvents)
    .filter((event) => selectedMonthsEvents[event] && selectedMonthsEvents[event].length)
    .map((item, index) => {
      const modifiedDay = item.length === 1 ? `0${item}` : item;
      const modifiedMonth = (selectedMonth + 1).toString().length === 1 ? `0${selectedMonth + 1}` : `${selectedMonth + 1}`;
      const day = moment(`${curYear}-${modifiedMonth}-${modifiedDay}`).format('dddd');
      const month = moment(`${curYear}-${modifiedMonth}-${modifiedDay}`).format('MMM');

      return (
        <div key={item} className="keen-slider__slide">
          <Header>
            <Date>
              {day}
              ,
              {' '}
              {month}
              {' '}
              {item}
            </Date>
            <LinkContainer>
              <Button
                onClick={() => [goToTop(), history.push('/cal/reminder')]}
                aria-label="go to the reminder page"
                tabIndex={(index === slideIndex) ? '0' : '-1'}
              >
                <InnerLink>{_('cal.set_edit_reminder')}</InnerLink>
              </Button>
            </LinkContainer>
          </Header>
          {
            selectedMonthsEvents[item].map((event) => {
              // Use local images directly instead of CDN URL
              const iconicShapeKey = collection[`collection-${event.id}`].iconicShape.replace('_', '-');
              const iconicShape = images.calendar[iconicShapeKey];
              const color = collection[`collection-${event.id}`].colour;
              const { title } = collection[`collection-${event.id}`];
              const { curbTime } = collection[`collection-${event.id}`];
              const id = `collection-${event.id}`;

              return (
                <Main
                  key={event.id}
                  onMouseDown={(e) => onMouseDownHandler(e)}
                  onClick={(e) => onClickHandler(e, id, collection, history, setIsHoliday())}
                  alt={`click to see ${title} event`}
                  title={`${title} event`}
                  tabIndex={(index === slideIndex) ? '0' : '-1'}
                >
                  <CollectionIcon
                    src={iconicShape}
                    alt
                    style={{ backgroundColor: (color) ? `#${color}` : '#000000' }}
                  />
                  <CollectionContent>
                    <Title>{title}</Title>
                    <Time>{curbTime}</Time>
                  </CollectionContent>
                  <CollectionArrow src={images.calendar.rightArrow} alt />
                </Main>
              );
            })
          }

        </div>
      );
    });

  const settings = {
    dots: true,
    arrow: false,
    infinite: false,
    speed: 1,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  return (
    <Container eventsContainerHeight={eventsContainerHeight}>
      <Slider {...settings} ref={sliderRef} afterChange={(e) => setSlideIndex(e)}>
        {content}
      </Slider>
    </Container>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setIsModalOpen: (isModalOpen) => dispatch(alertSetup.setIsModalOpen(isModalOpen)),
  setModalAlertingMessage: (modalAlertingMessage) => dispatch(
    alertSetup.setModalAlertingMessage(modalAlertingMessage)
  ),
});

export default connect(null, mapDispatchToProps)(EventSlider);
